import { Box } from '@mui/material';
import { useAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { ExportButton } from '@/components/ExportButton';
import ResizablePanel from '@/components/ResizablePanel';
import AlertRuleEditDialog from '@/features/AlertRuleEditDialog';
import AlertRulePanel from '@/features/AlertRulePanel';
import { AlertRulesSearch } from '@/features/AlertRulesSearch';
import AlertRulesTable from '@/features/AlertRulesTable';
import Header from '@/layout/Header/Header';
import MainContent from '@/layout/MainContent';
import { isLayoutMobileAtom } from '@/store/layout';

import {
  filteredAlertRulesAtom,
  isAlertRulesPanelOpenAtom,
  selectedAlertRuleIdAtom,
  useAlertRuleSelection,
  useFetchAlertRuleDetails,
  useFetchAlertRules
} from './AlertRules.store';
import useStyles from './AlertRules.styles';
import type { AlertRuleDTO } from './AlertRules.types';

const AlertRules = () => {
  const [filteredAlertRules] = useAtom(filteredAlertRulesAtom);
  const { classes } = useStyles();
  const { isFetching } = useFetchAlertRules();
  const [isAlertRulesPanelOpen, setIsAlertRulesPanelOpen] = useAtom(
    isAlertRulesPanelOpenAtom
  );
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedAlertRuleId, setSelectedAlertRuleId] = useAtom(
    selectedAlertRuleIdAtom
  );
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const { deselectAlertRule } = useAlertRuleSelection();
  const [searchParams] = useSearchParams();
  const alertRuleId = searchParams.get('alertRule');

  const { isLoading } = useFetchAlertRuleDetails();
  const sizes = ['75%', '25%'];

  useEffect(() => {
    // sync rule id from from url to atom value
    if (
      alertRuleId &&
      !isLoading &&
      filteredAlertRules.length > 0 &&
      filteredAlertRules.find((a) => a.trigger_id === alertRuleId)
    ) {
      setSelectedAlertRuleId(alertRuleId);
    }

    if (!alertRuleId && selectedAlertRuleId) {
      deselectAlertRule();
    }
  }, [searchParams, isLoading, filteredAlertRules]);

  useEffect(() => {
    // if there is alertRuleName param in the url or selectedAlertRule but it is not in the filtered alertRules - deselect alertRule
    // this check must be debounced because on initial page load - loading is false when data is loaded but filteredAlertRules is still empty and is set in the next tick
    const handler = setTimeout(() => {
      if (
        (alertRuleId || selectedAlertRuleId) &&
        filteredAlertRules.length > 0
      ) {
        const id = alertRuleId || selectedAlertRuleId;
        if (!filteredAlertRules.find((a) => a.trigger_id === id)) {
          deselectAlertRule();
        }
      }
    }, 100);

    return () => {
      clearTimeout(handler);
    };
  }, [filteredAlertRules, selectedAlertRuleId, searchParams]);

  const exportData = useMemo(() => {
    return filteredAlertRules.map((alertRule: AlertRuleDTO) => ({
      ID: alertRule.id,
      Name: alertRule.name,
      Description: alertRule.description,
      'Last Modified': alertRule.updated_at,
      'Last Triggered': alertRule.last_triggered_at,
      Repo: alertRule.repo,
      Enabled: alertRule.enabled ? 'Yes' : 'No',
      'AI Enabled': alertRule.ai_enabled ? 'Yes' : 'No'
    }));
  }, [filteredAlertRules]);

  const isPanelOpen = isAlertRulesPanelOpen && !isLayoutMobile;

  useEffect(() => {
    // on selected alert rule change - open or close the alert rule details panel
    if (selectedAlertRuleId) {
      setIsAlertRulesPanelOpen(true);
      return;
    }
    setIsAlertRulesPanelOpen(false);
  }, [selectedAlertRuleId]);

  const panels = useMemo(
    () => [
      {
        content: <AlertRulesTable isLoading={isFetching} />,
        id: 'alert-rules-table',
        order: 1,
        minSize: isPanelOpen ? '40%' : '0%',
        maxSize: isPanelOpen ? '75%' : '100%',
        showResizeHandle: isPanelOpen
      },
      {
        content: <AlertRulePanel setOpen={setOpenEditDialog} />,
        id: 'alert-rules-details',
        order: 2,
        minSize: '25%',
        maxSize: '40%',
        showResizeHandle: false
      }
    ],
    [isPanelOpen, isFetching]
  );

  return (
    <>
      <Header
        title="Alert Rules"
        actions={
          <ExportButton
            data={exportData}
            fileName="pondurance-alert-rules"
            json
            csv
          />
        }
      >
        <Box className={classes.alertRulesSearchWrapper}>
          <AlertRulesSearch />
        </Box>
      </Header>
      <MainContent>
        <Box className={classes.alertRulesResizablePanelWrapper}>
          <ResizablePanel
            panelGroupKey={isPanelOpen ? 'two-panels' : 'one-panel'}
            sizes={isPanelOpen ? sizes : ['100%']}
            panels={isPanelOpen ? panels : [panels[0]]}
          />
        </Box>
      </MainContent>
      {openEditDialog && (
        <AlertRuleEditDialog
          open={openEditDialog}
          onClose={() => setOpenEditDialog(false)}
        />
      )}
    </>
  );
};

export default AlertRules;
